import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { WeekdayIndex } from "../../../../../types/clientConfig";
import {
  FULL_WEEK,
  rotateWeekdayDisplayOrder,
  sortScheduleDays,
} from "./utils";

interface DaySchedulePickerProps {
  schedule: WeekdayIndex[];
  onChange: (schedule: WeekdayIndex[]) => void;
  disabled: boolean;
  /** 0 = Sun … 6 = Sat; only reorders which chip renders first. Defaults to Sunday-first. */
  startingCalendarDay?: number;
}

export const DaySchedulePicker = ({
  schedule,
  onChange,
  disabled: sectionIsDisabled,
  startingCalendarDay = 0,
}: DaySchedulePickerProps) => {
  const { t } = useTranslation("athlete-portal");
  const dayLabels = t("clientConfiguration.weekdays.short", {
    returnObjects: true,
  }) as string[];
  const displayDayOrder = useMemo(
    () => rotateWeekdayDisplayOrder(startingCalendarDay),
    [startingCalendarDay]
  );

  const selected = sortScheduleDays(schedule) || [];
  const isEveryday =
    selected.length === 7 && FULL_WEEK.every((d) => selected.includes(d));

  const toggleDay = (incomingDayIdx: WeekdayIndex) => {
    if (sectionIsDisabled) return;
    if (isEveryday) {
      onChange([incomingDayIdx]);
      return;
    }
    const dayAlreadyExists = selected.includes(incomingDayIdx);
    const newSchedule: WeekdayIndex[] = dayAlreadyExists
      ? selected.filter((existingIdx) => existingIdx !== incomingDayIdx)
      : [...selected, incomingDayIdx].sort((a, b) => a - b);
    if (newSchedule.length === 0 || newSchedule.length === 7) {
      onChange([...FULL_WEEK]);
    } else {
      onChange(newSchedule);
    }
  };

  return (
    <div className="scheduler-wrapper">
      <button
        type="button"
        disabled={sectionIsDisabled}
        onClick={() => !sectionIsDisabled && onChange([...FULL_WEEK])}
        className={`scheduler-everyday${isEveryday ? " active" : ""}`}
      >
        {t("clientConfiguration.schedule.everyday")}
      </button>
      <div className="scheduler-days">
        {displayDayOrder.map((dayIdx) => {
          const label = dayLabels[dayIdx];
          return (
            <button
              key={dayIdx}
              type="button"
              disabled={sectionIsDisabled}
              onClick={() => toggleDay(dayIdx)}
              className={`scheduler-day${
                selected.includes(dayIdx) ? " active" : ""
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
};
